import React, { FormEvent, useMemo, useState } from "react";
import { createRoot } from "react-dom/client";
import type { Root } from "react-dom/client";
import {
  AlertTriangle,
  Bot,
  CheckCircle2,
  Clipboard,
  Copy,
  Download,
  FileCode2,
  FileSearch,
  Globe2,
  Languages,
  Link2,
  Loader2,
  Network,
  Radar,
  RefreshCw,
  Search,
  ShieldCheck,
  SlidersHorizontal,
  Sparkles,
  XCircle
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { sampleAudit } from "./sampleAudit";
import type { BotPolicy, FileCheck, InspectResponse, Language, PolicyMode } from "./types";
import "./styles.css";

declare global {
  interface Window {
    __agentindexRoot?: Root;
  }
}

type SnippetKey = "llms" | "robots" | "report";

const labels = {
  en: {
    inspect: "Inspect",
    sample: "Sample",
    loading: "Auditing",
    title: "AgentIndex Lab",
    subtitle: "AI discovery and crawler policy console",
    urlLabel: "Public domain",
    urlPlaceholder: "https://docs.example.com",
    snapshot: "Audit snapshot",
    readiness: "Readiness",
    fileDiscovery: "File discovery",
    recommendations: "Priority fixes",
    noRecommendations: "No urgent fixes.",
    llmsEditor: "llms.txt builder",
    crawlerPolicy: "AI crawler policy",
    generated: "Deployment snippets",
    markdownReport: "Markdown audit report",
    copy: "Copy",
    download: "Download",
    copied: "Copied",
    copyFail: "Copy was not available in this browser.",
    titleField: "Site title",
    summaryField: "Summary",
    linksField: "Links",
    linkHint: "Label | URL | Section",
    balanced: "Balanced",
    open: "Open",
    restricted: "Restricted",
    good: "Good",
    warn: "Needs review",
    bad: "Missing",
    found: "Found",
    notFound: "Not found",
    headers: "Headers",
    sitemap: "Sitemap",
    policy: "Policy",
    source: "Source",
    intent: "Intent",
    status: "Status",
    issues: "Structure checks",
    links: "Curated links",
    preview: "Preview",
    publicFetch: "Public URL checks run through a Cloudflare Pages Function.",
    scoreNote: "Score combines llms.txt structure, discovery files, sitemap, headers, and crawler intent.",
    imageCaption: "Console target: dense audit data, editable policy, copy-ready outputs."
  },
  ko: {
    inspect: "검사",
    sample: "샘플",
    loading: "검사 중",
    title: "AgentIndex Lab",
    subtitle: "AI 발견성 및 크롤러 정책 콘솔",
    urlLabel: "공개 도메인",
    urlPlaceholder: "https://docs.example.com",
    snapshot: "감사 스냅샷",
    readiness: "준비도",
    fileDiscovery: "파일 발견",
    recommendations: "우선 수정",
    noRecommendations: "긴급 수정 없음",
    llmsEditor: "llms.txt 빌더",
    crawlerPolicy: "AI 크롤러 정책",
    generated: "배포 스니펫",
    markdownReport: "Markdown 감사 리포트",
    copy: "복사",
    download: "다운로드",
    copied: "복사됨",
    copyFail: "이 브라우저에서는 복사를 사용할 수 없습니다.",
    titleField: "사이트 제목",
    summaryField: "요약",
    linksField: "링크",
    linkHint: "라벨 | URL | 섹션",
    balanced: "균형",
    open: "전체 허용",
    restricted: "제한",
    good: "양호",
    warn: "검토 필요",
    bad: "누락",
    found: "발견",
    notFound: "없음",
    headers: "헤더",
    sitemap: "사이트맵",
    policy: "정책",
    source: "출처",
    intent: "목적",
    status: "상태",
    issues: "구조 검사",
    links: "선별 링크",
    preview: "미리보기",
    publicFetch: "공개 URL 검사는 Cloudflare Pages Function에서 실행됩니다.",
    scoreNote: "점수는 llms.txt 구조, 발견 파일, 사이트맵, 헤더, 크롤러 의도를 합산합니다.",
    imageCaption: "목표 콘솔: 조밀한 감사 데이터, 편집 가능한 정책, 복사 가능한 산출물."
  }
};

const statusMeta: Record<BotPolicy["status"], { icon: LucideIcon; tone: string; en: string; ko: string }> = {
  allowed: { icon: CheckCircle2, tone: "good", en: "Allowed", ko: "허용" },
  blocked: { icon: XCircle, tone: "bad", en: "Blocked", ko: "차단" },
  partial: { icon: AlertTriangle, tone: "warn", en: "Partial", ko: "일부 제한" },
  unspecified: { icon: SlidersHorizontal, tone: "neutral", en: "Unspecified", ko: "미지정" }
};

const snippetTabs: Array<{ key: SnippetKey; icon: LucideIcon; label: string }> = [
  { key: "llms", icon: FileCode2, label: "llms.txt" },
  { key: "robots", icon: ShieldCheck, label: "robots.txt" },
  { key: "report", icon: Clipboard, label: "audit.md" }
];

function App() {
  const [language, setLanguage] = useState<Language>("en");
  const [inputUrl, setInputUrl] = useState("https://docs.example.com");
  const [audit, setAudit] = useState<InspectResponse>(sampleAudit);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [copyState, setCopyState] = useState("");
  const [snippetTab, setSnippetTab] = useState<SnippetKey>("llms");
  const [policyMode, setPolicyMode] = useState<PolicyMode>("balanced");
  const [siteTitle, setSiteTitle] = useState(sampleAudit.llms.title);
  const [siteSummary, setSiteSummary] = useState(sampleAudit.llms.summary);
  const [linkRows, setLinkRows] = useState(linksToRows(sampleAudit));
  const t = labels[language];

  const generatedLlms = useMemo(
    () => buildLlmsText(siteTitle, siteSummary, linkRows, audit.origin),
    [audit.origin, linkRows, siteSummary, siteTitle]
  );
  const generatedRobots = useMemo(() => buildRobotsText(audit.origin, policyMode), [audit.origin, policyMode]);
  const markdownReport = useMemo(
    () => buildMarkdownReport(audit, generatedLlms, generatedRobots),
    [audit, generatedLlms, generatedRobots]
  );
  const activeSnippet = snippetTab === "llms" ? generatedLlms : snippetTab === "robots" ? generatedRobots : markdownReport;
  const score = computeReadiness(audit);
  const llmsFile = audit.files.find((file) => file.kind === "llms" && file.found);
  const displayedLlms = llmsFile?.body || generatedLlms;

  async function handleInspect(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setIsLoading(true);
    setError("");

    try {
      const response = await fetch(`/api/inspect?url=${encodeURIComponent(inputUrl)}`);
      const payload: unknown = await response.json();
      if (!response.ok || !isInspectResponse(payload)) {
        const message = extractError(payload) || "The inspection request failed.";
        throw new Error(message);
      }
      setAudit(payload);
      hydrateEditor(payload);
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : "The inspection request failed.");
    } finally {
      setIsLoading(false);
    }
  }

  function loadSample() {
    setInputUrl(sampleAudit.input);
    setAudit(sampleAudit);
    hydrateEditor(sampleAudit);
    setError("");
  }

  function hydrateEditor(nextAudit: InspectResponse) {
    setSiteTitle(nextAudit.llms.title || hostLabel(nextAudit.origin));
    setSiteSummary(nextAudit.llms.summary || `Official resources and high-value pages for ${hostLabel(nextAudit.origin)}.`);
    setLinkRows(linksToRows(nextAudit));
  }

  async function copyText(text: string, key: string) {
    try {
      await navigator.clipboard.writeText(text);
      setCopyState(key);
      window.setTimeout(() => setCopyState(""), 1500);
    } catch {
      setError(t.copyFail);
    }
  }

  function downloadText(text: string, fileName: string) {
    const blob = new Blob([text], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = fileName;
    anchor.click();
    URL.revokeObjectURL(url);
  }

  return (
    <main className="app-shell">
      <header className="topbar">
        <div className="brand">
          <div className="brand-mark" aria-hidden="true">
            <Bot size={25} />
          </div>
          <div>
            <p>{t.subtitle}</p>
            <h1>{t.title}</h1>
          </div>
        </div>

        <form className="inspect-form" onSubmit={handleInspect}>
          <label htmlFor="domain-input">{t.urlLabel}</label>
          <div className="input-wrap">
            <Globe2 size={18} />
            <input
              id="domain-input"
              value={inputUrl}
              placeholder={t.urlPlaceholder}
              onChange={(event) => setInputUrl(event.target.value)}
            />
          </div>
          <button className="primary-button" type="submit" disabled={isLoading}>
            {isLoading ? <Loader2 className="spin" size={18} /> : <Search size={18} />}
            {isLoading ? t.loading : t.inspect}
          </button>
        </form>

        <div className="top-actions">
          <button className="ghost-button" type="button" onClick={loadSample}>
            <RefreshCw size={17} />
            {t.sample}
          </button>
          <button
            className="icon-button"
            type="button"
            title={language === "en" ? "Korean" : "English"}
            onClick={() => setLanguage(language === "en" ? "ko" : "en")}
          >
            <Languages size={18} />
          </button>
        </div>
      </header>

      {error ? (
        <section className="error-strip" role="alert">
          <AlertTriangle size={18} />
          <span>{error}</span>
        </section>
      ) : null}

      <section className="workspace-grid">
        <aside className="left-rail">
          <PanelTitle icon={Radar} title={t.snapshot} />
          <div className="score-ring" style={{ "--score": `${score}%` } as React.CSSProperties}>
            <strong>{score}</strong>
            <span>{t.readiness}</span>
          </div>
          <p className="small-note">{t.scoreNote}</p>

          <div className="metric-row">
            <Metric label="llms.txt" value={`${audit.llms.score}/100`} />
            <Metric label={t.sitemap} value={audit.sitemap.present ? `${audit.sitemap.urlCount}` : "0"} />
            <Metric label={t.headers} value={`${audit.headers.advertisedLlms.length}`} />
          </div>

          <section className="rail-section">
            <h2>{t.fileDiscovery}</h2>
            <div className="file-list">
              {audit.files.map((file) => (
                <FileStatus key={file.kind} file={file} language={language} />
              ))}
            </div>
          </section>

          <section className="rail-section">
            <h2>{t.recommendations}</h2>
            <ul className="recommendations">
              {(audit.recommendations.length ? audit.recommendations : [t.noRecommendations]).map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </section>
        </aside>

        <section className="center-stack">
          <section className="panel editor-panel">
            <PanelTitle icon={FileSearch} title={t.llmsEditor} />
            <div className="editor-grid">
              <label>
                <span>{t.titleField}</span>
                <input value={siteTitle} onChange={(event) => setSiteTitle(event.target.value)} />
              </label>
              <label>
                <span>{t.summaryField}</span>
                <input value={siteSummary} onChange={(event) => setSiteSummary(event.target.value)} />
              </label>
            </div>
            <label className="wide-label">
              <span>
                {t.linksField} <em>{t.linkHint}</em>
              </span>
              <textarea value={linkRows} onChange={(event) => setLinkRows(event.target.value)} rows={5} />
            </label>
            <div className="llms-preview">
              <div className="code-head">
                <span>{t.preview}</span>
                <button className="mini-button" type="button" onClick={() => void copyText(generatedLlms, "llms")}>
                  <Copy size={15} />
                  {copyState === "llms" ? t.copied : t.copy}
                </button>
              </div>
              <pre>{generatedLlms}</pre>
            </div>
          </section>

          <section className="panel structure-panel">
            <div className="split-title">
              <PanelTitle icon={Network} title="llms.txt" />
              <div className="chip-line">
                <Chip tone={audit.llms.present ? "good" : "bad"}>{audit.llms.present ? t.found : t.notFound}</Chip>
                <Chip tone="neutral">{audit.llms.estimatedTokens} tokens</Chip>
                <Chip tone="neutral">{audit.llms.linkCount} links</Chip>
              </div>
            </div>
            <div className="structure-grid">
              <div>
                <h3>{t.issues}</h3>
                <ul className="issue-list">
                  {audit.llms.issues.map((issue) => (
                    <li key={`${issue.level}-${issue.message}`} className={`issue-${issue.level}`}>
                      {issue.level === "good" ? <CheckCircle2 size={16} /> : <AlertTriangle size={16} />}
                      <span>{issue.message}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <h3>{t.links}</h3>
                <div className="link-table">
                  {audit.llms.links.slice(0, 8).map((link) => (
                    <a data-google-vignette="false" key={`${link.href}-${link.text}`} href={link.href} target="_blank" rel="noreferrer">
                      <Link2 size={14} />
                      <span>{link.text}</span>
                      <small>{link.section}</small>
                    </a>
                  ))}
                  {audit.llms.links.length === 0 ? <p className="empty-text">No Markdown links detected.</p> : null}
                </div>
              </div>
            </div>
            <div className="source-preview">
              <div className="code-head">
                <span>Detected llms.txt</span>
                <button className="mini-button" type="button" onClick={() => void copyText(displayedLlms, "detected")}>
                  <Copy size={15} />
                  {copyState === "detected" ? t.copied : t.copy}
                </button>
              </div>
              <pre>{displayedLlms}</pre>
            </div>
          </section>
        </section>

        <aside className="right-rail">
          <section className="panel">
            <PanelTitle icon={ShieldCheck} title={t.crawlerPolicy} />
            <div className="policy-mode">
              {(["balanced", "open", "restricted"] as PolicyMode[]).map((mode) => (
                <button
                  key={mode}
                  className={policyMode === mode ? "active" : ""}
                  type="button"
                  onClick={() => setPolicyMode(mode)}
                >
                  {t[mode]}
                </button>
              ))}
            </div>
            <div className="policy-list">
              {audit.policies.map((policy) => (
                <PolicyRow key={policy.agent} policy={policy} language={language} />
              ))}
            </div>
          </section>

          <section className="panel concept-panel">
            <img src="/agentindex-console.png" alt="AgentIndex Lab audit console concept" />
            <figcaption>{t.imageCaption}</figcaption>
          </section>
        </aside>
      </section>

      <section className="output-band">
        <div className="output-head">
          <PanelTitle icon={Sparkles} title={snippetTab === "report" ? t.markdownReport : t.generated} />
          <div className="output-actions">
            <div className="snippet-tabs">
              {snippetTabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.key}
                    className={snippetTab === tab.key ? "active" : ""}
                    type="button"
                    onClick={() => setSnippetTab(tab.key)}
                  >
                    <Icon size={15} />
                    {tab.label}
                  </button>
                );
              })}
            </div>
            <button className="mini-button" type="button" onClick={() => void copyText(activeSnippet, snippetTab)}>
              <Copy size={15} />
              {copyState === snippetTab ? t.copied : t.copy}
            </button>
            <button
              className="mini-button"
              type="button"
              onClick={() => downloadText(activeSnippet, snippetTab === "report" ? "agentindex-audit.md" : `${snippetTab}.txt`)}
            >
              <Download size={15} />
              {t.download}
            </button>
          </div>
        </div>
        <pre className="output-code">{activeSnippet}</pre>
        <p className="small-note">{t.publicFetch}</p>
      </section>

      <footer className="site-footer">
        <span>Independent AI crawler policy helper. Public URLs only; no credentials are collected.</span>
        <nav aria-label="Site policies">
          <a data-google-vignette="false" href="/privacy.html">Privacy</a>
          <a data-google-vignette="false" href="/about.html">About</a>
        </nav>
      </footer>
    </main>
  );
}

function PanelTitle({ icon: Icon, title }: { icon: LucideIcon; title: string }) {
  return (
    <div className="panel-title">
      <Icon size={18} />
      <span>{title}</span>
    </div>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div className="metric">
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function Chip({ tone, children }: { tone: string; children: React.ReactNode }) {
  return <span className={`chip chip-${tone}`}>{children}</span>;
}

function FileStatus({ file, language }: { file: FileCheck; language: Language }) {
  const tone = file.found ? "good" : file.status === null || file.status >= 500 ? "bad" : "warn";
  const statusText = file.found ? labels[language].found : labels[language].notFound;
  return (
    <div className="file-row">
      <div className={`status-dot dot-${tone}`} />
      <span>{file.label}</span>
      <strong>{file.status ? `${file.status}` : "ERR"}</strong>
      <small>{statusText}</small>
    </div>
  );
}

function PolicyRow({ policy, language }: { policy: BotPolicy; language: Language }) {
  const meta = statusMeta[policy.status];
  const Icon = meta.icon;
  return (
    <div className="policy-row">
      <div>
        <strong>{policy.agent}</strong>
        <span>{policy.intent}</span>
      </div>
      <span>{policy.source}</span>
      <Chip tone={meta.tone}>
        <Icon size={14} />
        {meta[language]}
      </Chip>
    </div>
  );
}

function isInspectResponse(value: unknown): value is InspectResponse {
  if (!value || typeof value !== "object") {
    return false;
  }
  const record = value as Record<string, unknown>;
  return (
    typeof record.origin === "string" &&
    Array.isArray(record.files) &&
    typeof record.llms === "object" &&
    Array.isArray(record.policies)
  );
}

function extractError(value: unknown): string {
  if (!value || typeof value !== "object") {
    return "";
  }
  const record = value as Record<string, unknown>;
  return typeof record.error === "string" ? record.error : "";
}

function linksToRows(audit: InspectResponse): string {
  const rows = audit.llms.links.length
    ? audit.llms.links
    : [
        { text: "Getting started", href: `${audit.origin}/getting-started`, section: "Core resources" },
        { text: "API reference", href: `${audit.origin}/api/reference`, section: "Core resources" },
        { text: "Changelog", href: `${audit.origin}/changelog`, section: "Product guides" }
      ];
  return rows.map((link) => `${link.text} | ${link.href} | ${link.section}`).join("\n");
}

function buildLlmsText(title: string, summary: string, rows: string, origin: string): string {
  const parsedRows = rows
    .split(/\r?\n/)
    .map((row) => row.trim())
    .filter(Boolean)
    .map((row) => {
      const [label = "Resource", url = origin, section = "Core resources"] = row.split("|").map((part) => part.trim());
      const safeUrl = /^https?:\/\//i.test(url) ? url : new URL(url || "/", origin).toString();
      return { label, url: safeUrl, section: section || "Core resources" };
    });
  const grouped = parsedRows.reduce<Map<string, Array<{ label: string; url: string }>>>((groups, row) => {
    const list = groups.get(row.section) ?? [];
    list.push({ label: row.label, url: row.url });
    groups.set(row.section, list);
    return groups;
  }, new Map());

  const lines = [`# ${title || hostLabel(origin)}`, `> ${summary || `Official resources for ${hostLabel(origin)}.`}`, ""];
  for (const [section, links] of grouped.entries()) {
    lines.push(`## ${section}`);
    for (const link of links) {
      lines.push(`- [${link.label}](${link.url})`);
    }
    lines.push("");
  }
  return lines.join("\n").trim();
}

function buildRobotsText(origin: string, mode: PolicyMode): string {
  const searchAgents = ["OAI-SearchBot", "Claude-SearchBot", "PerplexityBot", "Googlebot"];
  const trainingAgents = ["GPTBot", "ClaudeBot", "Google-Extended"];
  const userAgents = ["Claude-User", "Perplexity-User"];
  const lines = ["User-agent: *", mode === "restricted" ? "Disallow: /" : "Allow: /", ""];

  const addGroup = (agent: string, allow: boolean) => {
    lines.push(`User-agent: ${agent}`, allow ? "Allow: /" : "Disallow: /", "");
  };

  for (const agent of searchAgents) {
    addGroup(agent, mode !== "restricted");
  }
  for (const agent of userAgents) {
    addGroup(agent, mode !== "restricted");
  }
  for (const agent of trainingAgents) {
    addGroup(agent, mode === "open");
  }
  lines.push(`Sitemap: ${origin}/sitemap.xml`);
  return lines.join("\n").trim();
}

function buildMarkdownReport(audit: InspectResponse, llmsText: string, robotsText: string): string {
  const fileRows = audit.files
    .map((file) => `| ${file.label} | ${file.found ? "found" : "missing"} | ${file.status ?? "error"} | ${file.url} |`)
    .join("\n");
  const policyRows = audit.policies
    .map((policy) => `| ${policy.agent} | ${policy.intent} | ${policy.status} | ${policy.source} |`)
    .join("\n");
  const notes = audit.recommendations.map((item) => `- ${item}`).join("\n");
  return `# AgentIndex Audit - ${hostLabel(audit.origin)}

Audited: ${new Date(audit.checkedAt).toLocaleString()}
Origin: ${audit.origin}
Readiness: ${computeReadiness(audit)}/100

## File Status
| File | Result | HTTP | URL |
| --- | --- | --- | --- |
${fileRows}

## llms.txt
- Structure score: ${audit.llms.score}/100
- Title: ${audit.llms.title || "missing"}
- Sections: ${audit.llms.sections.join(", ") || "none"}
- Links: ${audit.llms.linkCount}
- Estimated tokens: ${audit.llms.estimatedTokens}

## AI Crawler Policy
| Crawler | Intent | Status | Source |
| --- | --- | --- | --- |
${policyRows}

## Recommendations
${notes || "- No urgent recommendations."}

## Suggested llms.txt
\`\`\`txt
${llmsText}
\`\`\`

## Suggested robots.txt
\`\`\`txt
${robotsText}
\`\`\``;
}

function computeReadiness(audit: InspectResponse): number {
  const requiredKinds = ["llms", "robots", "sitemap"] as const;
  const fileScore =
    (requiredKinds.filter((kind) => audit.files.some((file) => file.kind === kind && file.found)).length /
      requiredKinds.length) *
    30;
  const headerScore = Math.min(audit.headers.advertisedLlms.length * 8, 10);
  const llmsScore = audit.llms.score * 0.45;
  const searchPolicies = audit.policies.filter((policy) => policy.intent === "search");
  const policyScore =
    (searchPolicies.filter((policy) => policy.status === "allowed" || policy.status === "partial").length /
      Math.max(searchPolicies.length, 1)) *
    15;
  return Math.round(Math.min(100, fileScore + headerScore + llmsScore + policyScore));
}

function hostLabel(origin: string): string {
  try {
    return new URL(origin).hostname;
  } catch {
    return origin.replace(/^https?:\/\//, "");
  }
}

const rootElement = document.getElementById("root");
if (!rootElement) {
  throw new Error("Root element is missing.");
}

window.__agentindexRoot?.unmount();
window.__agentindexRoot = createRoot(rootElement);
window.__agentindexRoot.render(<App />);
